import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { reportError } from '../utils/errorHandler';

// P1-PERF-4: LRU cache with max size and TTL-based refresh
const MAX_CACHE_SIZE = 500;
const CACHE_TTL_MS = 60 * 1000; // 60 seconds
const userProfileCache = new Map(); // uid -> { data, at }

function evictIfNeeded() {
	if (userProfileCache.size <= MAX_CACHE_SIZE) return;
	// Evict oldest entries by timestamp
	const entries = Array.from(userProfileCache.entries());
	entries.sort((a, b) => (a[1].at || 0) - (b[1].at || 0));
	const toEvict = entries.slice(0, entries.length - MAX_CACHE_SIZE);
	for (const [uid] of toEvict) {
		userProfileCache.delete(uid);
	}
}

function sanitizeProfile(data) {
	if (!data || typeof data !== 'object') return {};
	const { email, ...rest } = data;
	return rest;
}

async function fetchMergedProfile(uid) {
	let data = {};
	try {
		const snap = await getDoc(doc(db, 'publicProfiles', uid));
		data = snap.exists() ? (snap.data() || {}) : {};
	} catch (err) {
		reportError(err, { userMessage: 'User profile fetch failed', toast: false });
		data = {};
	}
	// Normalize legacy field name if present
	if (data && !data.photoURL && data.photoUrl) {
		data.photoURL = data.photoUrl;
	}
	// Never expose email via public profile cache
	if (data && data.email) {
		delete data.email;
	}
	return sanitizeProfile(data);
}

export async function getUserProfileCached(uid) {
	if (!uid) return {};
	try {
		const hit = userProfileCache.get(uid);
		const now = Date.now();
		if (hit && (now - (hit.at || 0)) < CACHE_TTL_MS) {
			return hit.data || {};
		}
		const data = await fetchMergedProfile(uid);
		userProfileCache.set(uid, { data, at: now });
		evictIfNeeded();
		return data || {};
	} catch (err) {
		reportError(err, { userMessage: 'User profile cache failed', toast: false });
		return {};
	}
}

export async function prefetchUserProfiles(uids) {
	const unique = Array.from(new Set((uids || []).filter(Boolean)));
	await Promise.all(unique.map(async (uid) => {
		const entry = userProfileCache.get(uid);
		const now = Date.now();
		const isStale = !entry || (now - entry.at) >= CACHE_TTL_MS;
		if (isStale) {
			const data = await fetchMergedProfile(uid);
			userProfileCache.set(uid, { data, at: now });
		}
	}));
	evictIfNeeded();
}

export function primeUserProfile(uid, data) {
	if (!uid) return;
	userProfileCache.set(uid, { data: sanitizeProfile(data || {}), at: Date.now() });
	evictIfNeeded();
}

export function clearUserProfileCache() {
	userProfileCache.clear();
}
